import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { allArticles } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const article = allArticles.find((a) => a.slug === slug) || allArticles[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const articleSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: 'https://yoybuyspreadsheet.cyou',
    },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yoybuyspreadsheet.cyou/article/${article.slug}`,
    },
  }), [article]);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: 'https://yoybuyspreadsheet.cyou/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `https://yoybuyspreadsheet.cyou/article/${article.slug}`,
      },
    ],
  }), [article]);

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <main>
        <article className="pt-24 pb-20 md:pt-28 md:pb-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-8"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              {t('article.back_home')}
            </Link>

            <header className="mb-12">
              <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 rounded-full text-xs font-label uppercase tracking-wider mb-4">
                SEO Guide
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                <span>{article.meta}</span>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden mb-12">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            <div className="space-y-10">
              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 tracking-tight">
                    {section.heading}
                  </h2>
                  <p className="text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                </section>
              ))}
            </div>

            <div className="mt-16 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                Другие полезные статьи
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {allArticles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-background-200/70 hover:border-background-300/60 transition-colors cursor-pointer group"
                    >
                      <div className="w-12 h-12 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors">
                          {a.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">{a.meta.split('|')[0].trim()}</p>
                      </div>
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 group-hover:text-accent-500 transition-colors">
                        <i className="ri-arrow-right-s-line text-sm"></i>
                      </span>
                    </Link>
                  ))}
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                {t('footer.home')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-foreground-950 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-sm"></i>
                </span>
                kaspreadsheet.com
              </a>
            </div>

            <div className="mt-12 p-6 md:p-8 bg-white rounded-2xl border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                FAQ по YOYBUY Spreadsheet
              </h3>
              <div className="space-y-2">
                <div className="border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-50/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-900">{t('faq.q1')}</span>
                    <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 transition-transform duration-300 ${openFaq === 0 ? 'rotate-45' : ''}`}>
                      <i className="ri-add-line"></i>
                    </span>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${openFaq === 0 ? 'max-h-[300px] pb-4 px-4' : 'max-h-0'}`}>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t('faq.a1')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}